import { useState, useEffect } from "react";
import {
    FaUser, FaUserShield, FaChevronRight, FaChevronLeft, FaCheck,
    FaPlus, FaBuilding, FaLock, FaInfoCircle, FaTimes
} from "react-icons/fa";
import axios from "@/api/api";
import { API_ROUTES } from "@/api/routes";
import { toast } from "react-toastify";
import FormularioPerfil, { ALCANCES } from "@/components/FormularioPerfil";

export default function WizardUsuario({ onClose, usuarioEditar = null }) {
    const [paso, setPaso] = useState(1);
    const [modoCreacionPerfil, setModoCreacionPerfil] = useState(false);
    const [perfilesDisponibles, setPerfilesDisponibles] = useState([]);
    const [empresasDisponibles, setEmpresasDisponibles] = useState([]);
    const [loading, setLoading] = useState(false);

    // Estado del formulario de usuario
    const [formUsuario, setFormUsuario] = useState({
        usuario: "",
        contrasena: "",
        nombre_completo: "",
        empresa_id: "",
        perfil_id: ""
    });

    // Estado del formulario de perfil (si se crea uno nuevo)
    const [formPerfil, setFormPerfil] = useState({
        nombre: "",
        configuracion: {
            ver_todo: false,
            alcance: "cliente",
            modulosVisibles: [],
            permisos: {},
            menus_sidebar: []
        }
    });

    useEffect(() => {
        cargarDatos();
        if (usuarioEditar) {
            setFormUsuario({
                usuario: usuarioEditar.usuario,
                contrasena: "",
                nombre_completo: usuarioEditar.nombre_completo || usuarioEditar.nombre,
                empresa_id: usuarioEditar.empresa_id,
                perfil_id: usuarioEditar.perfil_id
            });
        }
    }, [usuarioEditar]);

    const cargarDatos = async () => {
        try {
            const [perfilesRes, empresasRes] = await Promise.all([
                axios.get(API_ROUTES.perfiles), // ✅ CAMBIO: Usar misma ruta que GestionPerfiles
                axios.get(API_ROUTES.usuariosEmpresas)
            ]);

            console.log('📦 Perfiles recibidos en WizardUsuario:', perfilesRes.data);

            // Parsear configuracion si viene como string
            let perfilesData = Array.isArray(perfilesRes.data) ? perfilesRes.data : [];
            perfilesData = perfilesData.map(perfil => {
                console.log(`🔍 Procesando perfil ${perfil.nombre}:`, perfil);

                // Intentar obtener la configuración de diferentes campos posibles
                let config = perfil.configuracion || perfil.config_json || null;

                // Si es string, parsear
                if (typeof config === 'string' && config.trim()) {
                    try {
                        config = JSON.parse(config);
                        console.log(`✅ Config parseada para ${perfil.nombre}:`, config);
                    } catch (e) {
                        console.error(`❌ Error al parsear config de ${perfil.nombre}:`, e);
                        config = null;
                    }
                }

                // Si config es null o no es un objeto válido, usar estructura por defecto
                if (!config || typeof config !== 'object') {
                    console.log(`⚠️ Config vacío para ${perfil.nombre}, usando default`);
                    config = {
                        ver_todo: false,
                        alcance: "cliente",
                        modulosVisibles: [],
                        permisos: {},
                        menus_sidebar: []
                    };
                }

                return {
                    ...perfil,
                    configuracion: config
                };
            });

            console.log('✅ Perfiles procesados en WizardUsuario:', perfilesData);
            setPerfilesDisponibles(perfilesData);
            setEmpresasDisponibles(Array.isArray(empresasRes.data) ? empresasRes.data : []);
        } catch (error) {
            console.error("Error al cargar datos:", error);
            toast.error("Error al cargar perfiles y empresas");
            setPerfilesDisponibles([]);
            setEmpresasDisponibles([]);
        }
    };

    const guardarPerfilYContinuar = async () => {
        if (!formPerfil.nombre.trim()) {
            toast.error("El nombre del perfil es requerido");
            return;
        }

        try {
            setLoading(true);
            const response = await axios.post(API_ROUTES.perfilCreate, {
                nombre: formPerfil.nombre,
                configuracion: JSON.stringify(formPerfil.configuracion)
            });

            toast.success("Perfil creado exitosamente");

            // Asignar el perfil recién creado al usuario
            setFormUsuario({
                ...formUsuario,
                perfil_id: response.data.id
            });

            // Actualizar lista de perfiles
            await cargarDatos();

            // Volver al paso 1 (selección de perfil) o ir al paso 2
            setModoCreacionPerfil(false);
            setPaso(2);
        } catch (error) {
            console.error("Error al crear perfil:", error);
            toast.error("Error al crear el perfil");
        } finally {
            setLoading(false);
        }
    };

    const guardarUsuario = async () => {
        // Validaciones
        if (!formUsuario.usuario.trim()) {
            toast.error("El usuario es requerido");
            return;
        }
        if (!usuarioEditar && !formUsuario.contrasena.trim()) {
            toast.error("La contraseña es requerida");
            return;
        }
        if (!formUsuario.nombre_completo.trim()) {
            toast.error("El nombre completo es requerido");
            return;
        }
        if (!formUsuario.perfil_id) {
            toast.error("Debes seleccionar un perfil");
            return;
        }

        try {
            setLoading(true);

            const usuarioData = {
                usuario: formUsuario.usuario,
                nombre_completo: formUsuario.nombre_completo,
                empresa_id: formUsuario.empresa_id || null,
                perfil_id: formUsuario.perfil_id
            };

            // Solo incluir contraseña si no está vacía
            if (formUsuario.contrasena.trim()) {
                usuarioData.contrasena = formUsuario.contrasena;
            }

            if (usuarioEditar) {
                await axios.put(API_ROUTES.usuarioUpdate(usuarioEditar.id), usuarioData);
                toast.success("Usuario actualizado correctamente");
            } else {
                await axios.post(API_ROUTES.usuarioCreate, usuarioData); // ✅ CORREGIDO: usuarioCreate (singular)
                toast.success("Usuario creado correctamente");
            }

            onClose(true); // Cerrar y recargar
        } catch (error) {
            console.error("Error al guardar usuario:", error);
            toast.error(error.response?.data?.message || "Error al guardar el usuario");
        } finally {
            setLoading(false);
        }
    };

    const perfilSeleccionado = perfilesDisponibles.find(p => p.id === Number(formUsuario.perfil_id));

    return (
        <div
            className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose(false);
                }
            }}
        >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header con stepper */}
                <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-white">
                            {usuarioEditar ? "Editar Usuario" : "Crear Nuevo Usuario"}
                        </h2>
                        <button
                            onClick={() => onClose(false)}
                            className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                        >
                            <FaTimes size={24} />
                        </button>
                    </div>

                    {/* Stepper */}
                    <div className="flex items-center justify-center gap-4">
                        {/* Paso 1: Perfil */}
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${paso === 1
                                    ? 'bg-white text-cyan-600 scale-110'
                                    : formUsuario.perfil_id
                                        ? 'bg-green-500 text-white'
                                        : 'bg-white/30 text-white'
                                }`}>
                                {formUsuario.perfil_id ? <FaCheck /> : '1'}
                            </div>
                            <div className="text-white">
                                <div className="font-semibold">Paso 1</div>
                                <div className="text-sm text-cyan-100">Asignar Perfil</div>
                            </div>
                        </div>

                        <FaChevronRight className="text-white/50" />

                        {/* Paso 2: Usuario */}
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${paso === 2
                                    ? 'bg-white text-cyan-600 scale-110'
                                    : 'bg-white/30 text-white'
                                }`}>
                                2
                            </div>
                            <div className="text-white">
                                <div className="font-semibold">Paso 2</div>
                                <div className="text-sm text-cyan-100">Datos del Usuario</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contenido */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* PASO 1: Selección/Creación de Perfil */}
                    {paso === 1 && (
                        <div className="space-y-6">
                            {!modoCreacionPerfil ? (
                                <>
                                    <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                                        <div className="flex items-start gap-4 mb-4">
                                            <FaInfoCircle className="text-blue-600 text-2xl mt-1" />
                                            <div>
                                                <h3 className="font-bold text-blue-900 text-lg mb-2">
                                                    Selecciona un perfil existente
                                                </h3>
                                                <p className="text-blue-700 text-sm">
                                                    Elige uno de los perfiles ya configurados o crea uno nuevo si necesitas permisos específicos.
                                                </p>
                                            </div>
                                        </div>

                                        {/* Lista de perfiles disponibles */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {perfilesDisponibles.map((perfil) => {
                                                const alcance = ALCANCES.find(a => a.value === perfil.configuracion?.alcance);
                                                const numModulos = perfil.configuracion?.modulosVisibles?.length || 0;
                                                const numMenus = perfil.configuracion?.menus_sidebar?.length || 0;

                                                return (
                                                    <button
                                                        key={perfil.id}
                                                        onClick={() => setFormUsuario({ ...formUsuario, perfil_id: perfil.id })}
                                                        className={`text-left p-4 rounded-lg border-2 transition-all ${formUsuario.perfil_id === perfil.id
                                                                ? 'border-cyan-500 bg-cyan-50 shadow-lg scale-105'
                                                                : 'border-gray-200 bg-white hover:border-cyan-300 hover:shadow-md'
                                                            }`}
                                                    >
                                                        <div className="flex items-start justify-between mb-2">
                                                            <div className="font-bold text-gray-800">{perfil.nombre}</div>
                                                            {formUsuario.perfil_id === perfil.id && (
                                                                <FaCheck className="text-cyan-600" />
                                                            )}
                                                        </div>
                                                        <div className="text-sm text-gray-600 mb-3">
                                                            {alcance?.label || 'No definido'}
                                                        </div>
                                                        <div className="flex gap-4 text-xs">
                                                            <span className="text-blue-600">
                                                                📦 {numModulos} Módulos
                                                            </span>
                                                            <span className="text-green-600">
                                                                📱 {numMenus} Menús
                                                            </span>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Botón para crear nuevo perfil */}
                                    <button
                                        onClick={() => setModoCreacionPerfil(true)}
                                        className="w-full p-6 border-2 border-dashed border-cyan-400 rounded-xl bg-cyan-50 hover:bg-cyan-100 transition-all text-cyan-700 font-semibold flex items-center justify-center gap-3"
                                    >
                                        <FaPlus className="text-xl" />
                                        <span>Crear Nuevo Perfil</span>
                                    </button>
                                </>
                            ) : (
                                <div className="space-y-6">
                                    <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-3">
                                                <FaUserShield className="text-purple-600 text-2xl" />
                                                <div>
                                                    <h3 className="font-bold text-purple-900 text-lg">
                                                        Configurar Nuevo Perfil
                                                    </h3>
                                                    <p className="text-purple-700 text-sm">
                                                        Define los permisos y accesos para este perfil
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => setModoCreacionPerfil(false)}
                                                className="text-purple-600 hover:bg-purple-100 px-4 py-2 rounded-lg transition-colors"
                                            >
                                                Cancelar
                                            </button>
                                        </div>
                                    </div>

                                    {/* Formulario de Perfil usando el componente reutilizable */}
                                    <FormularioPerfil
                                        formData={formPerfil}
                                        onChange={setFormPerfil}
                                        showNombreField={true}
                                        showJsonPreview={false}
                                        onJsonPreviewToggle={null}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* PASO 2: Datos del Usuario */}
                    {paso === 2 && (
                        <div className="space-y-6">
                            {/* Mostrar perfil seleccionado */}
                            {perfilSeleccionado && (
                                <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
                                    <div className="flex items-center gap-3">
                                        <FaCheck className="text-green-600 text-2xl" />
                                        <div>
                                            <div className="font-bold text-green-900">
                                                Perfil asignado: {perfilSeleccionado.nombre}
                                            </div>
                                            <div className="text-sm text-green-700">
                                                {ALCANCES.find(a => a.value === perfilSeleccionado.configuracion?.alcance)?.label || 'No definido'}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => setPaso(1)}
                                            className="ml-auto text-green-600 hover:bg-green-100 px-4 py-2 rounded-lg transition-colors text-sm"
                                        >
                                            Cambiar
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Formulario de datos del usuario */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Usuario */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Usuario (Username) *
                                    </label>
                                    <div className="relative">
                                        <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            value={formUsuario.usuario}
                                            onChange={(e) => setFormUsuario({ ...formUsuario, usuario: e.target.value })}
                                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                            placeholder="usuario123"
                                        />
                                    </div>
                                </div>

                                {/* Contraseña */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Contraseña {!usuarioEditar && "*"}
                                    </label>
                                    <div className="relative">
                                        <FaLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="password"
                                            value={formUsuario.contrasena}
                                            onChange={(e) => setFormUsuario({ ...formUsuario, contrasena: e.target.value })}
                                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                            placeholder={usuarioEditar ? "Dejar vacío para no cambiar" : "********"}
                                        />
                                    </div>
                                </div>

                                {/* Nombre Completo */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Nombre Completo *
                                    </label>
                                    <div className="relative">
                                        <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            value={formUsuario.nombre_completo}
                                            onChange={(e) => setFormUsuario({ ...formUsuario, nombre_completo: e.target.value })}
                                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                            placeholder="Juan Pérez"
                                        />
                                    </div>
                                </div>

                                {/* Empresa (Opcional) */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Empresa (Opcional)
                                    </label>
                                    <div className="relative">
                                        <FaBuilding className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <select
                                            value={formUsuario.empresa_id}
                                            onChange={(e) => setFormUsuario({ ...formUsuario, empresa_id: e.target.value })}
                                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                        >
                                            <option value="">Sin empresa</option>
                                            {empresasDisponibles.map((empresa) => (
                                                <option key={empresa.id} value={empresa.id}>
                                                    {empresa.nombre}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer con botones de navegación */}
                <div className="border-t bg-gray-50 p-6">
                    <div className="flex justify-between">
                        {/* Botón Atrás */}
                        {paso > 1 && !modoCreacionPerfil && (
                            <button
                                onClick={() => setPaso(paso - 1)}
                                className="px-6 py-3 text-gray-700 bg-white border-2 border-gray-300 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2"
                            >
                                <FaChevronLeft /> Atrás
                            </button>
                        )}

                        <div className="ml-auto"></div>

                        {/* Botones de acción según el paso */}
                        {paso === 1 && !modoCreacionPerfil && (
                            <button
                                onClick={() => setPaso(2)}
                                disabled={!formUsuario.perfil_id}
                                className="px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-lg font-semibold hover:from-cyan-700 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Continuar <FaChevronRight />
                            </button>
                        )}

                        {paso === 1 && modoCreacionPerfil && (
                            <button
                                onClick={guardarPerfilYContinuar}
                                disabled={loading}
                                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl flex items-center gap-2 disabled:opacity-50"
                            >
                                {loading ? "Guardando..." : "Guardar Perfil y Continuar"} <FaChevronRight />
                            </button>
                        )}

                        {paso === 2 && (
                            <button
                                onClick={guardarUsuario}
                                disabled={loading}
                                className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-semibold hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl flex items-center gap-2 disabled:opacity-50"
                            >
                                <FaCheck /> {loading ? "Guardando..." : usuarioEditar ? "Actualizar Usuario" : "Crear Usuario"}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}